import { useId, type ReactNode } from 'react';

import { cn } from './cn.js';
import { Icon } from './Icon.js';
import { useDialogBehavior } from './useDialogBehavior.js';

export interface SheetProps {
  /** Heading text. Also names the dialog for assistive tech, so it is required. */
  title: string;
  onClose: () => void;
  /**
   * Accessible name for the close control (backdrop and header button alike). `packages/ui` has no
   * `@mypoz/i18n` dependency and never calls `t()` itself — same as `title`, this must already be
   * translated by the caller. Required for the same reason `title` is: an untranslated fallback
   * here would just recreate the hardcoded string this prop exists to remove.
   */
  closeLabel: string;
  /** Whether Escape and the backdrop close the sheet. Defaults to true — see `Modal`'s doc comment. */
  dismissible?: boolean;
  /** Changing this re-places focus inside the panel — see `Modal`'s `focusKey` doc comment. */
  focusKey?: string | number;
  /** Extra classes on the panel. */
  className?: string;
  /**
   * Pinned under the scrolling body, so its actions stay in view however long the content gets
   * (MYP-117). A form's submit button can live here and still submit it via the `form` attribute.
   */
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * A panel anchored to an edge rather than centred: the right edge from `sm:` up, the bottom edge
 * below it — a bottom sheet on a phone, a slide-over on anything wider (MYP-33).
 *
 * Shares `Modal`'s focus-trap/Escape/scroll-lock/focus-restore plumbing via `useDialogBehavior`
 * rather than reimplementing it — see that hook's doc comment. What differs from `Modal` is only
 * shape: an edge-anchored panel sized to the viewport's short edge, not a centred card sized to
 * its content.
 *
 * `NotificationThreadPanel` is a right-edge panel that predates this and doesn't drop to a bottom
 * sheet on mobile; it's left as-is rather than migrated here, since reaching for a genuine focus
 * trap on a panel that's read rather than acted in is a judgment call that ticket didn't need to
 * make. New edge-anchored panels that need real editing controls should reach for this instead.
 */
export function Sheet({
  title,
  onClose,
  closeLabel,
  dismissible = true,
  focusKey,
  className,
  footer,
  children,
}: SheetProps) {
  const titleId = useId();
  const { panelRef } = useDialogBehavior<HTMLElement>({ dismissible, onClose, focusKey });

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end">
      {dismissible ? (
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-[1px]"
        />
      ) : (
        <div aria-hidden="true" className="absolute inset-0 bg-black/45 backdrop-blur-[1px]" />
      )}

      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cn(
          'relative flex w-full flex-col overflow-hidden bg-surface shadow-raised outline-none',
          // Bottom sheet below `sm:`: rounded top corners, capped height, content scrolls inside.
          'max-h-[85vh] rounded-t-xl',
          // Right-edge panel from `sm:` up: full height, fixed width, square inner corner.
          'sm:h-full sm:max-h-none sm:w-[min(94vw,28rem)] sm:rounded-none',
          className,
        )}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-line px-5 py-4">
          <h2 id={titleId} className="text-lg font-semibold text-ink">
            {title}
          </h2>
          {dismissible ? (
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className="-mr-1.5 flex size-9 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-surface-alt hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              <Icon name="close" size={22} />
            </button>
          ) : null}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <div className="shrink-0 border-t border-line bg-surface px-5 py-3">{footer}</div>
        ) : null}
      </aside>
    </div>
  );
}
